import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  REFUND_STATUSES,
  SUPPORT_LANGUAGES,
  SUPPORT_LANGUAGE_LABELS,
  type RefundStatus,
} from '@capsico/contracts';
import { useI18n } from '../i18n';
import { chatMessages, statusLabel } from '../i18n/supportLabels';
import { refundRowView } from '../i18n/labels';
import { supportBubbles, useTicketChat } from '../hooks/useSupport';
import { useMyOrder } from '../hooks/useMyOrders';
import { useRefundChoice } from '../hooks/useRefunds';
import {
  ChatComposer,
  ChatThread,
  CsatCard,
  DeskNotice,
  PillChoice,
  RatedLine,
  RefundLine,
  SupportDraftBar,
  TicketStatusCard,
} from '../components/Support';
import { RefundsCard } from '../components/Payment';
import { Button, Card, EmptyState, ListSkeleton, Notice, PageHeader } from '../components/ui';
import { money } from '../utils/money';

/**
 * Help chat (W16): status + ETA / queue position / agent, off-hours notice, language switch,
 * "Talk to an agent", refund outcome cards (Capsico Money or the original method where the
 * refund still waits for a choice), the thread (assistant + agents, live with a 15 s poll
 * fallback), the 1–5 rating after resolve, and the reply box. No typing indicators.
 */
export function HelpChatPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { ticketId = '' } = useParams();
  const c = useTicketChat(ticketId, 'customer', chatMessages(t));
  const view = c.view;
  const order = useMyOrder(view?.orderId ?? '', { poll: false });
  const country = order.order?.country;
  const refundChoice = useRefundChoice(
    { orderId: view?.orderId ?? undefined, country },
    { saved: t('refund.saved') },
  );
  const bubbles = useMemo(
    () =>
      supportBubbles(view?.messages ?? [], 'customer', {
        bot: t('help.bot'),
        agent: t('help.agent'),
        team: t('help.team'),
      }),
    [view?.messages, t],
  );

  const header = (
    <PageHeader
      title={view ? t('help.chatTitle', { number: view.number }) : t('help.title')}
      onBack={() => navigate(-1)}
      backLabel={t('common.back')}
    />
  );

  if (c.loading) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        <div className="px-4 pt-5">
          <ListSkeleton rows={5} />
        </div>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="min-h-dvh bg-page">
        {header}
        {c.notFound ? (
          <EmptyState icon="info" title={t('help.notFound')} />
        ) : (
          <div className="px-4 pt-5">
            <Notice
              tone="negative"
              supportRef
              title={t('help.loadFailed')}
              action={
                <Button size="sm" tone="outline" onClick={c.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const refundIds = new Set(view.refunds.map((refund) => refund.refundId));
  const orderRefunds = (order.order?.payment.refunds ?? []).filter((refund) =>
    refundIds.has(refund.id),
  );
  const known = new Set(orderRefunds.map((refund) => refund.id));
  const otherRefunds = view.refunds.filter((refund) => !known.has(refund.refundId));
  const lines = [
    view.etaText,
    view.queuePosition ? t('help.queue', { n: view.queuePosition }) : null,
    view.agentName && view.status === 'assigned'
      ? t('help.agentReplying', { name: view.agentName })
      : null,
  ].filter((line): line is string => Boolean(line));

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      {header}
      <div className="flex flex-1 flex-col gap-4 px-4 pt-4 pb-6">
        <TicketStatusCard
          title={view.reasonLabel}
          subtitle={view.orderNumber ? t('help.orderLabel', { number: view.orderNumber }) : null}
          status={view.status}
          statusLabel={statusLabel(t, view.status)}
          lines={lines}
        >
          {c.open && (
            <div className="mt-3 space-y-3">
              <PillChoice
                label={t('help.language')}
                value={c.language}
                options={SUPPORT_LANGUAGES.map((code) => ({
                  value: code,
                  label: SUPPORT_LANGUAGE_LABELS[code],
                }))}
                onChange={c.setLanguage}
              />
              {c.canAskAgent && (
                <Button tone="outline" full size="sm" busy={c.askingAgent} onClick={c.askAgent}>
                  {t('help.talkToAgent')}
                </Button>
              )}
            </div>
          )}
        </TicketStatusCard>

        {c.reconnecting && <Notice tone="warning" title={t('help.reconnecting')} />}
        {!view.deskOpen && c.open && (
          <DeskNotice
            tone="warning"
            icon="clock"
            title={t('help.deskClosed')}
            body={view.etaText}
          />
        )}

        {orderRefunds.length > 0 && country && (
          <RefundsCard
            title={t('help.refunds')}
            rows={orderRefunds.map((refund) => refundRowView(t, country, refund))}
            busyId={refundChoice.choosingId}
            onChoose={refundChoice.choose}
          />
        )}
        {otherRefunds.length > 0 && (
          <Card>
            <h2 className="text-sm font-bold text-ink">{t('help.refunds')}</h2>
            <div className="mt-1 divide-y divide-hair">
              {otherRefunds.map((refund) => (
                <RefundLine
                  key={refund.refundId}
                  amount={money(country, refund.amountMinor)}
                  caption={
                    refund.destination === 'wallet'
                      ? t('refund.toWallet')
                      : refund.destination === 'source'
                        ? t('refund.toSource')
                        : ''
                  }
                  status={
                    (REFUND_STATUSES as readonly string[]).includes(refund.status)
                      ? t(`refund.status.${refund.status as RefundStatus}`)
                      : refund.status
                  }
                />
              ))}
            </div>
          </Card>
        )}

        <ChatThread
          bubbles={bubbles}
          emptyText={t('help.empty')}
          sendingLabel={t('help.sending')}
        />

        {view.canRate ? (
          <CsatCard
            title={t('help.rateTitle')}
            body={t('help.rateBody')}
            score={c.csat.score}
            onScore={c.setScore}
            comment={c.csat.comment}
            onComment={c.setComment}
            canSubmit={c.canSubmitRating}
            busy={c.rating}
            onSubmit={c.submitRating}
            labels={{
              star: (n) => t('help.star', { n }),
              comment: t('help.rateComment'),
              submit: t('help.rateSubmit'),
            }}
            draft={
              c.csatDraftRestored ? (
                <SupportDraftBar
                  text={t('common.draftRestored')}
                  keepLabel={t('common.keep')}
                  discardLabel={t('common.discard')}
                  onKeep={c.keepCsatDraft}
                  onDiscard={c.discardCsatDraft}
                />
              ) : undefined
            }
          />
        ) : view.csatScore !== null ? (
          <RatedLine text={t('help.ratedLine', { score: view.csatScore })} />
        ) : null}
        {!view.canReply && <Notice tone="info" title={t('help.closedNotice')} />}
      </div>

      {view.canReply && (
        <div className="sticky bottom-0 bg-surface pb-[env(safe-area-inset-bottom)] shadow-[inset_0_1px_0_var(--color-hair)]">
          <div className="space-y-2 px-4 py-3">
            {c.draftRestored && (
              <SupportDraftBar
                text={t('common.draftRestored')}
                keepLabel={t('common.keep')}
                discardLabel={t('common.discard')}
                onKeep={c.keepDraft}
                onDiscard={c.discardDraft}
              />
            )}
            <ChatComposer
              text={c.text}
              onText={c.setText}
              maxLength={c.maxLength}
              canSend={c.canSend}
              sending={c.sending}
              onSend={c.send}
              slots={c.photos.slots}
              canAttach={c.photos.canAdd}
              onFile={c.photos.add}
              onRemove={c.photos.remove}
              labels={{
                placeholder: t('help.placeholder'),
                send: t('help.send'),
                attach: t('help.attach'),
                remove: t('help.removePhoto'),
                failed: t('help.photoFailed'),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
